import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getPurchaseHistory } from '../../../models/assetsModel';
import { useHasAnyPermission } from '../../../utils/permissions';
import Button from '../../../components/common/Button';

function PurchaseHistory() {
  const canViewAssets = useHasAnyPermission(['View Assets']);
  const [data, setData] = useState({
    assets: [],
    total_cost: 0,
    categories: []
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    asset_category_id: '',
    start: '',
    end: ''
  });

  useEffect(() => {
    fetchPurchaseHistory();
  }, []);

  const fetchPurchaseHistory = async () => {
    try {
      setLoading(true);
      const result = await getPurchaseHistory(filters);
      setData(result);
    } catch (error) {
      toast.error('Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchPurchaseHistory();
  };

  // Check permissions after all hooks are defined
  if (!canViewAssets) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Access Denied</div>
          <div className="text-gray-600">You do not have permission to view assets.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card - Following COMPONENT_DESIGN_PATTERNS.md */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Purchase History</h1>
                <p className="text-xs text-gray-500 mb-1">Track and analyze asset purchase history and costs</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    Showing {data.assets?.length || 0} of {data.assets?.length || 0} results
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Controls and Actions */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={fetchPurchaseHistory}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        
          <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={filters.asset_category_id}
              onChange={(e) => setFilters({...filters, asset_category_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {data.categories?.map(cat => (
                <option key={cat.category_id} value={cat.category_id}>
                  {cat.category_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={filters.start}
              onChange={(e) => setFilters({...filters, start: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={filters.end}
              onChange={(e) => setFilters({...filters, end: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={handleFilter} className="w-full">
              Filter
            </Button>
          </div>
        </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Purchase Summary</h3>
            <p className="text-sm text-gray-500 mt-1">Overview of assets and total costs</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center bg-gray-50 rounded-lg border p-6">
                <div className="text-3xl font-bold text-blue-600">{data.assets?.length || 0}</div>
                <div className="text-gray-600">Total Assets</div>
              </div>
              <div className="text-center bg-gray-50 rounded-lg border p-6">
                <div className="text-3xl font-bold text-green-600">
                  R {data.total_cost ? parseFloat(data.total_cost).toLocaleString() : '0'}
                </div>
                <div className="text-gray-600">Total Value</div>
              </div>
              <div className="text-center bg-gray-50 rounded-lg border p-6">
                <div className="text-3xl font-bold text-purple-600">
                  R {data.assets?.length > 0 ? Math.round(data.total_cost / data.assets.length).toLocaleString() : '0'}
                </div>
                <div className="text-gray-600">Average Cost</div>
              </div>
            </div>
          </div>
        </div>

        {/* Assets Table */}
        <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Asset Name
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Category
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Purchase Date
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Cost
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Vendor
                  </th>
                </tr>
              </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-3 py-2 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : data.assets?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-3 py-2 text-center text-gray-500">
                    No assets found
                  </td>
                </tr>
              ) : (
                data.assets?.map((asset, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {asset.asset_name}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {asset.category}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {asset.asset_date_purchased}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      R {parseFloat(asset.asset_cost || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {asset.vendor}
                    </td>
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseHistory;
